import { Badge, Inline, Stack, Text } from '@amboss/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../config';
import { useAppSelector } from '../../hooks/redux';
import { CreditType } from '../../interfaces/credit';
import type { ParsStatus } from '../../interfaces/parsStatus';
import { creditsTypeSelector } from '../../store/credits/creditsSlice';
import { getColorFromParsStatus, getLabelFromStatus } from '../../utils';
import Tooltip from './Tooltip';

type ItemCourseProps = {
  courseId: string;
  courseTitle: string;
  courseSubtitle?: string;
  parsStatus?: ParsStatus;
  parsResponse?: any;
};

const ItemCourse = ({
  courseId,
  courseTitle,
  courseSubtitle,
  parsStatus,
  parsResponse,
}: ItemCourseProps) => {
  const { t } = useTranslation();
  const creditType = useAppSelector(creditsTypeSelector);

  const tooltipText = useMemo(() => {
    if (parsStatus === config.parsStatus.NOT_SUBMITTED) {
      return t('credits.tabs.tab3.tooltip.notSubmitted');
    }
    if (parsStatus === config.parsStatus.REJECTED) {
      return parsResponse
        ? `${parsResponse.errorMessage} (Error ${parsResponse.errorCode})`
        : t('credits.tabs.tab3.tooltip.rejected');
    }
    return '';
  }, [parsStatus, parsResponse]);

  const showStatusBadge = parsStatus && parsResponse && creditType !== CreditType.ABFM;

  return (
    <Stack space={'xs'}>
      <Inline>
        <Text>{courseTitle}</Text>
      </Inline>
      <Inline>
        <Text color={'tertiary'}>{courseSubtitle ?? '-'}</Text>
        {showStatusBadge && (
          <span data-tip data-for={courseId}>
            <Badge
              color={getColorFromParsStatus(parsStatus as ParsStatus)}
              text={getLabelFromStatus(parsStatus)}
            />
            {parsStatus !== config.parsStatus.ACCEPTED && (
              <Tooltip
                text={`${t('credits.tabs.tab3.tooltip.general')} ${tooltipText}`}
                id={courseId}
              />
            )}
          </span>
        )}
      </Inline>
    </Stack>
  );
};

export default ItemCourse;
